import { useState } from 'react';
import { useParams } from 'react-router-dom';
import type { SignupRequest } from '../domain/account';
import { isDomainError } from '../domain/errors';
import { useRepositories } from '../hooks/useRepositories';
import { EmptyState } from '../components/PageStates';
import SignUpForm from '../components/SignUpForm';
import { Hourglass } from 'lucide-react';

/**
 * `/invite/:token/sign-up` — make an account through a link. The server
 * signs this browser in, so success is a whole-page load of the invite, which
 * Django then serves signed in; Apple and Google come back to it too (019
 * FR-009).
 */
export default function InviteSignUpPage({
  assign = (url: string) => window.location.assign(url),
}: {
  /** The full-page navigation; injected by tests. */
  assign?: (url: string) => void;
}) {
  const { token = '' } = useParams<{ token: string }>();
  const { invitations, session } = useRepositories();
  const [expired, setExpired] = useState(false);
  const invitePath = `/invite/${token}`;

  if (expired) {
    return (
      <div className="mx-auto max-w-xl">
        <EmptyState
          icon={<Hourglass className="size-8" />}
          title="This invitation has expired."
          description="Ask whoever sent it for a new link."
        />
      </div>
    );
  }

  const signUp = async (request: SignupRequest) => {
    try {
      await invitations.signUpThroughLink(token, request);
    } catch (err) {
      if (isDomainError(err, 'invitationExpired')) {
        setExpired(true);
        return;
      }
      throw err;
    }
    assign(invitePath);
  };

  return (
    <div className="mx-auto max-w-md">
      <section className="rounded-3xl bg-card p-8">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Create your account</h1>
        <p className="mb-6 mt-1 text-sm text-muted-foreground">
          Then you'll go straight back to your invitation.
        </p>
        <SignUpForm onSubmit={signUp} next={invitePath} />
        <p className="mt-6 text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <a
            href={session.signInUrl(invitePath)}
            className="font-semibold text-primary hover:underline"
          >
            Sign in
          </a>
        </p>
      </section>
    </div>
  );
}
